import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Save } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import { APIEndpoint } from 'common/enum';
import { addressFormInterface } from 'common/interface';
import { universalGetRequest } from 'common/requestHandler';
import CustomModal from 'components/CustomModal';
import GradientButton from 'components/elements/GradientButton';
import InputElement from 'components/elements/InputElement';
import { popupNotification } from 'notifications/ToastNotification';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { validateGroupName } from 'validation/validationScheme';

// import { AddInvoiceCompanyBankDetails } from './';
// import { formatAddresses, saveTemplate } from '../utils';
import { AddInvoiceCompanyBankDetails } from '../../types';
import { saveTemplate, transformToCompanyAddressInterface } from '../../utils';
import AddCompany from './AddCompany';
import BankDetails from './AddCompanyBankAccounts';
import AddTermsAndCondition from './AddTermsAndCondition';

interface accordionInterface {
  company: boolean;
  account: boolean;
  termsAndConditions: boolean;
}
interface groupNameInterface {
  groupName: string;
}
const CreateTemplates = () => {
  const { companyId } = useSelector((state: RootState) => state.auth);
  const [showModal, setShowModal] = useState(false);
  const [templateName, setTemplateName] = useState('test');
  const [companyList, setCompanyList] = useState<addressFormInterface[]>([]);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAccordion, setShowAccordion] = useState({
    company: true,
    account: true,
    termsAndConditions: true,
  } as accordionInterface);
  const [bankList, setBankList] = useState(
    [] as AddInvoiceCompanyBankDetails[]
  );
  const handleAddCompany = (data: addressFormInterface) => {
    setCompanyList((prevCompanyList) => [
      ...prevCompanyList,
      { ...data, address: transformToCompanyAddressInterface(data) },
    ]);
  };
  const { control, handleSubmit, reset } = useForm<groupNameInterface>({
    resolver: yupResolver(validateGroupName),
    mode: 'onChange',
    defaultValues: {
      groupName: '',
    } as groupNameInterface,
  });

  const handleDeleteCompany = (id: string) => {
    setCompanyList((prevCompanyList) =>
      prevCompanyList.filter((company) => company.companyName !== id)
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  const handleAddBank = (data: any) => {
    setBankList((prev) => [...prev, data]);
  };
  const handleSaveTemplate = async (data: groupNameInterface) => {
    setLoading(true);
    const payload = {
      httpMethod: 'POST',
      actionName: 'CreateTemplate',
      companyId,
      templateName: data.groupName,
      templateDetails: {
        address: companyList,
        termsAndConditions,
        paymentTerms: ['Immediate', 'Net 30', 'Net 40', 'Net 50'],
        bankAccountDetails: bankList,
      },
    };
    await saveTemplate(payload);
    setLoading(false);
    setShowModal(false);
  };
  const getExistingData = async () => {
    const response: any = await universalGetRequest(
      APIEndpoint.getSecDocsMain,
      {
        companyId,
        httpMethod: 'GET',
        actionName: 'GetInvoiceTemplate',
      }
    );
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      setCompanyList(response.data?.address || []);
      setBankList(response.data?.bankAccountDetails || []);
      setTermsAndConditions(response?.data?.termsAndConditions);
    }
  };
  useEffect(() => {
    getExistingData();
  }, []);

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2} mt={2}>
        <Accordion
          expanded={showAccordion.company}
          onChange={() =>
            setShowAccordion((prev) => ({ ...prev, company: !prev.company }))
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography component="span" fontWeight="bold">
              Company details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AddCompany
              handleAddCompany={handleAddCompany}
              companyList={companyList}
              handleDeleteCompany={handleDeleteCompany}
            />
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={showAccordion.account}
          onChange={() =>
            setShowAccordion((prev) => ({ ...prev, account: !prev.account }))
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography component="span" fontWeight="bold">
              Account details
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <BankDetails
              setBankList={setBankList}
              bankList={bankList}
              handleAddBank={handleAddBank}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={showAccordion.termsAndConditions}
          onChange={() =>
            setShowAccordion((prev) => ({
              ...prev,
              termsAndConditions: !prev.termsAndConditions,
            }))
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel3-content"
            id="panel3-header"
          >
            <Typography component="span" fontWeight="bold">
              Terms and conditions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AddTermsAndCondition
              termsAndConditions={termsAndConditions}
              setTermsAndConditions={setTermsAndConditions}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
      <Tooltip title="Save template" placement="top" arrow>
        <Box
          className="floating-save-template-btn"
          onClick={() => setShowModal(true)}
        >
          <GradientButton rounded>
            <Save />
          </GradientButton>
        </Box>
      </Tooltip>
      <CustomModal
        primaryButtonAction={handleSubmit(handleSaveTemplate)}
        primaryButtonLabel="Save template"
        autoClose={false}
        secondaryButtonAction={() => {
          setShowModal(false);
          reset();
        }}
        primaryButtonLoading={loading}
        open={showModal}
        secondaryButtonLabel="Cancel"
        type="info"
        onClose={() => {
          setShowModal(false);
          reset();
        }}
        title="Template name"
      >
        <form onSubmit={handleSubmit(handleSaveTemplate)}>
          <Typography variant="body2" color="textDisabled">
            {' '}
            Type the template name
          </Typography>
          <InputElement
            name="groupName"
            size="small"
            control={control}
            label="Template name"
            autoFocus
            type="text"
            rules={{ required: true }} // Rules can be added here
          />
        </form>
      </CustomModal>
    </>
  );
};

export default CreateTemplates;
